import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, Bot, Sparkles } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { ChatSidebar } from '@/components/ai/ChatSidebar';
import { ChatMessage } from '@/components/ai/ChatMessage';
import { ChatComposer } from '@/components/ai/ChatComposer';
import { useAiModels } from '@/hooks/useAiModels';
import { useReminders } from '@/hooks/useReminders';
import { aiApi, streamChatMessage } from '@/utils/aiApi';
import '@/styles/ai.css';
import '@/styles/chat.css';

const SUGGESTIONS = [
  "What's overdue or due this week?",
  'Who has the most on their plate right now?',
  'Summarize our latest meeting in three bullets',
  'Draft a short status update for the team',
];

export default function AIChat({ user }) {
  const { models, selected: accountModel, nameOf } = useAiModels();
  const remindersState = useReminders();
  const [searchParams, setSearchParams] = useSearchParams();
  const [teams, setTeams] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [chats, setChats] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [conversation, setConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState('');
  const [streaming, setStreaming] = useState(null); // { id, content, model }
  const [sending, setSending] = useState(false);
  const [model, setModel] = useState(null);
  const [teamScope, setTeamScope] = useState('all');
  const [showList, setShowList] = useState(true);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const abortRef = useRef(null);
  const endRef = useRef(null);

  useEffect(() => {
    aiApi.chats().then(setChats).catch(() => toast.error('Could not load your chats'));
    aiApi.teams().then(setTeams).catch(() => {});
    aiApi.contacts().then(setContacts).catch(() => {});
  }, []);

  useEffect(() => { if (!activeId && accountModel && !model) setModel(accountModel); }, [accountModel, activeId, model]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' }); }, [messages, streaming?.content]);
  useEffect(() => () => abortRef.current?.abort(), []);

  const bumpChat = useCallback((id, patch) => {
    setChats((prev) => {
      const existing = prev.find((c) => c.id === id);
      const updated = { ...(existing || {}), ...patch, id };
      return [updated, ...prev.filter((c) => c.id !== id)];
    });
  }, []);

  const openChat = useCallback(async (id) => {
    if (sending) abortRef.current?.abort();
    try {
      const { conversation: conv, messages: history } = await aiApi.chat(id);
      setActiveId(id);
      setConversation(conv);
      setMessages(history);
      setModel(conv.model);
      setTeamScope(conv.team_id || 'all');
      setStreaming(null);
      setShowList(false);
    } catch {
      toast.error('Could not open that chat');
    }
  }, [sending]);

  // Deep link from a reminder message: /dashboard/chat?c=<chat id>
  useEffect(() => {
    const target = searchParams.get('c');
    if (!target) return;
    openChat(target);
    setSearchParams({}, { replace: true });
  }, [searchParams, setSearchParams, openChat]);

  const newChat = () => {
    if (sending) abortRef.current?.abort();
    setActiveId(null);
    setConversation(null);
    setMessages([]);
    setStreaming(null);
    setModel(accountModel);
    setShowList(false);
  };

  const changeModel = async (next) => {
    setModel(next);
    if (activeId) {
      try { await aiApi.updateChat(activeId, { model: next }); bumpChat(activeId, { model: next }); } catch { toast.error('Could not change the model'); }
    }
  };

  const changeTeam = async (next) => {
    setTeamScope(next);
    if (activeId) {
      try { await aiApi.updateChat(activeId, { team_id: next === 'all' ? null : next }); } catch { toast.error('Could not change the team scope'); }
    }
  };

  const send = async (textOverride) => {
    const text = (textOverride ?? draft).trim();
    if (!text || sending) return;
    setSending(true);
    setDraft('');
    let chatId = activeId;
    try {
      if (!chatId) {
        const conv = await aiApi.createChat({ model, team_id: teamScope === 'all' ? null : teamScope });
        chatId = conv.id;
        setActiveId(conv.id);
        setConversation(conv);
        bumpChat(conv.id, conv);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not start the chat');
      setSending(false);
      setDraft(text);
      return;
    }

    const tempId = `local-${Date.now()}`;
    setMessages((prev) => [...prev, { id: tempId, role: 'user', content: text, created_at: new Date().toISOString() }]);
    const controller = new AbortController();
    abortRef.current = controller;
    let assistantId = null;
    let partial = '';
    try {
      await streamChatMessage(chatId, {
        content: text,
        model,
        signal: controller.signal,
        onEvent: (ev) => {
          if (ev.type === 'start') {
            assistantId = ev.message_id;
            setMessages((prev) => prev.map((m) => (m.id === tempId ? ev.user_message : m)));
            setStreaming({ id: ev.message_id, content: '', model: ev.model });
            if (ev.title) { setConversation((c) => (c ? { ...c, title: ev.title } : c)); bumpChat(chatId, { title: ev.title, updated_at: new Date().toISOString(), model }); }
          } else if (ev.type === 'delta') {
            partial += ev.content;
            setStreaming((s) => (s ? { ...s, content: s.content + ev.content } : s));
          } else if (ev.type === 'done') {
            setMessages((prev) => [...prev, ev.message]);
            setStreaming(null);
            bumpChat(chatId, { updated_at: ev.message.created_at });
          } else if (ev.type === 'error') {
            toast.error(ev.detail || 'The model could not answer');
            setStreaming(null);
          }
        },
      });
    } catch (error) {
      if (error.name === 'AbortError') {
        if (partial) setMessages((prev) => [...prev, { id: assistantId || `stopped-${Date.now()}`, role: 'assistant', content: `${partial}\n\n_(stopped)_`, model, created_at: new Date().toISOString(), local: true }]);
        setStreaming(null);
      } else {
        toast.error(error.message || 'Sending failed');
        setStreaming(null);
      }
    } finally {
      setSending(false);
      abortRef.current = null;
    }
  };

  const removeChat = async () => {
    const target = confirmDelete;
    setConfirmDelete(null);
    try {
      await aiApi.deleteChat(target.id);
      setChats((prev) => prev.filter((c) => c.id !== target.id));
      if (target.id === activeId) newChat();
      toast.success('Chat deleted');
    } catch {
      toast.error('Could not delete the chat');
    }
  };

  const title = conversation?.title || 'New chat';
  const activeModelName = nameOf(model);

  return (
    <div className={`chat-page ${showList ? 'list-open' : ''}`} data-testid="ai-chat-page">
      <ChatSidebar chats={chats} activeId={activeId} onSelect={openChat} onNew={newChat} onDelete={setConfirmDelete} reminders={remindersState.reminders} onCancelReminder={remindersState.cancel} />

      <section className="chat-main" aria-label="Conversation">
        <header className="chat-header">
          <button type="button" className="chat-back mobile-only" onClick={() => setShowList(true)} aria-label="Back to chats" data-testid="chat-back-button">
            <ArrowLeft size={18} />
          </button>
          <div className="chat-header-text">
            <h1 className="chat-title" data-testid="chat-title">{title}</h1>
            <p className="chat-subtitle">ChatGPT · {activeModelName || '…'} · knows your tasks, teams and meetings</p>
          </div>
          <Select value={teamScope} onValueChange={changeTeam}>
            <SelectTrigger className="chat-scope" aria-label="Team scope" data-testid="chat-team-scope">
              <SelectValue />
            </SelectTrigger>
            <SelectContent align="end">
              <SelectItem value="all">All my teams</SelectItem>
              {teams.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </header>

        <div className="chat-thread" data-testid="chat-thread">
          {!messages.length && !streaming ? (
            <div className="chat-empty" data-testid="chat-empty">
              <div className="chat-empty-mark"><Bot size={28} /></div>
              <h2>How can I help, {user?.name?.split(' ')[0] || 'there'}?</h2>
              <p>I can see your teams, tasks and meeting summaries — ask about them, or about anything else.</p>
              <div className="chat-suggestions">
                {SUGGESTIONS.map((s, i) => (
                  <button key={s} type="button" className="chat-suggestion" onClick={() => send(s)} data-testid={`chat-suggestion-${i}`}>
                    <Sparkles size={13} /> {s}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <>
              {messages.map((m, i) => (
                <ChatMessage
                  key={m.id}
                  message={m}
                  modelName={nameOf(m.model)}
                  userName={user?.name}
                  user={user}
                  teams={teams}
                  contacts={contacts}
                  chatId={activeId}
                  onReminderCreated={remindersState.add}
                  teamId={teamScope === 'all' ? null : teamScope}
                  question={m.role === 'assistant' ? messages.slice(0, i).reverse().find((x) => x.role === 'user')?.content : undefined}
                />
              ))}
              {streaming && <ChatMessage message={{ id: streaming.id, role: 'assistant', content: streaming.content }} modelName={nameOf(streaming.model)} streaming />}
            </>
          )}
          <div ref={endRef} />
        </div>

        <ChatComposer
          value={draft}
          onChange={setDraft}
          onSend={() => send()}
          onStop={() => abortRef.current?.abort()}
          sending={sending}
          models={models}
          model={model}
          onModelChange={changeModel}
        />
      </section>

      <ConfirmDialog
        open={Boolean(confirmDelete)}
        onOpenChange={(open) => !open && setConfirmDelete(null)}
        title="Delete this chat?"
        description="The conversation and its messages will be removed. This cannot be undone."
        onConfirm={removeChat}
        testId="delete-chat-confirm"
      />
    </div>
  );
}
